import React, { useEffect, useState } from "react";
import Shell from "../components/Shell";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Check, Pencil, X, KeyRound, Activity } from "lucide-react";
import { useTheme } from "../lib/theme";
import SpiderLogo from "../components/SpiderLogo";
import { Switch } from "../components/ui/switch";
import { Volume2 } from "lucide-react";
import { isSoundEnabled, setSoundEnabled, playThwip } from "../lib/sound";
import { getCategoryMeta } from "../lib/categoryIcons";

const SUITS = [
  { id: "classic", name: "Classic Red", tag: "The web-slinger", primary: "#DC1F26", navy: "#061638", desc: "Amazing Spider-Man red & navy" },
  { id: "symbiote", name: "Symbiote Black", tag: "Feel the bond", primary: "#0A0A0A", navy: "#050505", desc: "Sleek black-suit stealth mode" },
  { id: "miles", name: "Miles Morales", tag: "Brooklyn stitched", primary: "#E11D48", navy: "#1E1B4B", desc: "Deep purple-navy with crimson pop" },
];

export default function Settings() {
  const { user, setUser } = useAuth();
  const { suit, setSuit } = useTheme();
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [name, setName] = useState(user?.name || "");
  const [budget, setBudget] = useState(String(user?.monthly_budget || 2000));
  const [cats, setCats] = useState([]);
  const [allCats, setAllCats] = useState({ expense: [], income: [] });
  const [budgets, setBudgets] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [merchantRules, setMerchantRules] = useState([]);
  // Auto-sync suggestions the user has hidden per-debt (now persisted server-side)
  const LEGACY_DISMISS_KEY = "argent-debt-sync-dismissed";
  const [hiddenDebts, setHiddenDebts] = useState([]); // [{id, name}]
  const refreshHiddenDebts = async () => {
    let ids = [];
    try {
      const r = await api.get("/preferences/sync-dismissed");
      ids = r.data?.debt_ids || [];
      // Migrate any legacy localStorage once
      try {
        const raw = localStorage.getItem(LEGACY_DISMISS_KEY);
        if (raw) {
          const legacyIds = JSON.parse(raw) || [];
          for (const id of legacyIds) {
            if (!ids.includes(id)) {
              try {
                await api.post("/preferences/sync-dismissed", { debt_id: id });
                ids.push(id);
              } catch {}
            }
          }
          localStorage.removeItem(LEGACY_DISMISS_KEY);
        }
      } catch {}
    } catch {
      try {
        const raw = localStorage.getItem(LEGACY_DISMISS_KEY);
        ids = raw ? JSON.parse(raw) : [];
      } catch { ids = []; }
    }
    if (!ids.length) { setHiddenDebts([]); return; }
    try {
      const r = await api.get("/debts");
      const named = (r.data || []).filter(d => ids.includes(d.id)).map(d => ({ id: d.id, name: d.name }));
      const missing = ids.filter(id => !named.some(n => n.id === id)).map(id => ({ id, name: "Deleted debt" }));
      setHiddenDebts([...named, ...missing]);
    } catch { setHiddenDebts([]); }
  };
  const restoreHiddenDebt = async (id) => {
    try { await api.delete(`/preferences/sync-dismissed/${id}`); }
    catch { toast.error("Couldn't restore — try again"); return; }
    refreshHiddenDebts();
    toast.success("Auto-sync restored");
  };
  const restoreAllHidden = async () => {
    try { await api.delete("/preferences/sync-dismissed"); }
    catch { toast.error("Couldn't restore — try again"); return; }
    refreshHiddenDebts();
    toast.success("All auto-sync suggestions restored");
  };
  const [newCat, setNewCat] = useState("");
  const [newLimit, setNewLimit] = useState("");
  const [rType, setRType] = useState("expense");
  const [rAmt, setRAmt] = useState("");
  const [rCat, setRCat] = useState("");
  const [rDesc, setRDesc] = useState("");
  const [rFreq, setRFreq] = useState("monthly");
  const [rNext, setRNext] = useState(new Date().toISOString().slice(0, 10));
  const [editingRecId, setEditingRecId] = useState(null);
  const [rulePattern, setRulePattern] = useState("");
  const [ruleCategory, setRuleCategory] = useState("");
  const [editingRuleKey, setEditingRuleKey] = useState(null);

  // Password change (in-app, signed-in flow)
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const changePassword = async () => {
    if (!currentPw || !newPw) { toast.error("Both current and new password are required"); return; }
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { toast.error("New passwords don't match"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPw, new_password: newPw });
      toast.success("Password updated");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change password");
    } finally { setPwSaving(false); }
  };

  const ADMIN_EMAIL = "isaiahwilli33@gmail.com";
  const isAdmin = (user?.email || "").toLowerCase() === ADMIN_EMAIL;

  // System status (uptime watchdog history) — admin-only telemetry
  const [health, setHealth] = useState(null);
  const [pings, setPings] = useState([]);
  const refreshHealth = async () => {
    if (!isAdmin) return;
    try {
      const [h, hist] = await Promise.all([
        api.get("/health"),
        api.get("/health/history?limit=30"),
      ]);
      setHealth(h.data);
      setPings(hist.data?.pings || []);
    } catch {
      setHealth({ status: "unknown", db: "unknown" });
    }
  };


  const cur = user?.currency || "USD";

  const load = async () => {
    const [c, b, r, m] = await Promise.all([
      api.get("/categories"),
      api.get("/budgets"),
      api.get("/recurring"),
      api.get("/merchant-rules"),
    ]);
    setCats(c.data.expense);
    setAllCats(c.data);
    setBudgets(b.data);
    setRecurring(r.data);
    setMerchantRules(m.data || []);
  };
  useEffect(() => { load(); refreshHiddenDebts(); }, []);
  // Fetch health only when user is loaded and is admin
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshHealth(); }, [user?.email]);
  useEffect(() => {
    setName(user?.name || "");
    setBudget(String(user?.monthly_budget || 2000));
  }, [user]);

  const saveProfile = async () => {
    try {
      const r = await api.patch("/user/settings", { name, monthly_budget: parseFloat(budget) });
      setUser(r.data);
      toast.success("Saved");
    } catch { toast.error("Failed"); }
  };
  const saveBudget = async () => {
    if (!newCat || !newLimit) { toast.error("Category and limit required"); return; }
    await api.post("/budgets", { category: newCat, monthly_limit: parseFloat(newLimit) });
    setNewCat(""); setNewLimit(""); load(); toast.success("Budget saved");
  };
  const delBudget = async (id) => { await api.delete(`/budgets/${id}`); load(); };
  const resetRecForm = () => {
    setRType("expense");
    setRAmt("");
    setRCat("");
    setRDesc("");
    setRFreq("monthly");
    setRNext(new Date().toISOString().slice(0, 10));
    setEditingRecId(null);
  };
  const freqLabel = (f) => ({
    daily: "daily",
    weekly: "weekly",
    biweekly: "bi-weekly",
    monthly: "monthly",
    quarterly: "quarterly",
    annually: "annually",
  }[f] || f);
  const startEditRecurring = (r) => {
    setEditingRecId(r.id);
    setRType(r.type);
    setRAmt(String(r.amount));
    setRCat(r.category);
    setRDesc(r.description || "");
    setRFreq(r.frequency);
    // Convert stored ISO datetime to yyyy-mm-dd for the date input
    try {
      setRNext(new Date(r.next_run).toISOString().slice(0, 10));
    } catch {
      setRNext(new Date().toISOString().slice(0, 10));
    }
  };
  const saveRecurring = async () => {
    if (!rAmt || !rCat) { toast.error("Amount and category required"); return; }
    const payload = {
      type: rType,
      amount: parseFloat(rAmt),
      category: rCat,
      description: rDesc,
      frequency: rFreq,
      next_run: new Date(rNext).toISOString(),
    };
    try {
      if (editingRecId) {
        await api.put(`/recurring/${editingRecId}`, payload);
        toast.success("Recurring updated");
      } else {
        await api.post("/recurring", payload);
        toast.success("Recurring saved");
      }
      resetRecForm();
      load();
    } catch {
      toast.error("Failed");
    }
  };
  const delRecurring = async (id) => {
    await api.delete(`/recurring/${id}`);
    if (editingRecId === id) resetRecForm();
    load();
  };
  const runRecurring = async () => {
    try {
      const r = await api.post("/recurring/run");
      if (r.data.created > 0) toast.success(`${r.data.created} recurring transaction${r.data.created > 1 ? "s" : ""} posted`);
      else toast("Nothing due right now");
      load();
    } catch { toast.error("Failed"); }
  };

  const resetRuleForm = () => {
    setRulePattern("");
    setRuleCategory("");
    setEditingRuleKey(null);
  };
  const startEditRule = (r) => {
    setEditingRuleKey(r.merchant_key);
    setRulePattern(r.pattern || r.merchant_key);
    setRuleCategory(r.category);
  };
  const saveRule = async () => {
    if (!rulePattern.trim() || !ruleCategory) {
      toast.error("Pattern and category required");
      return;
    }
    try {
      // If editing and the merchant_key changed (user renamed the pattern
      // significantly), delete the old row so we don't leave a duplicate.
      const newKey = rulePattern
        .toLowerCase()
        .match(/[a-z]{3,}/g)?.slice(0, 2).join(" ") || "";
      if (editingRuleKey && newKey && newKey !== editingRuleKey) {
        try { await api.delete(`/merchant-rules/${encodeURIComponent(editingRuleKey)}`); } catch { /* ignore */ }
      }
      await api.post("/merchant-rules", { pattern: rulePattern.trim(), category: ruleCategory });
      resetRuleForm();
      load();
      toast.success(editingRuleKey ? "Rule updated" : "Rule saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save rule");
    }
  };
  const deleteRule = async (key) => {
    await api.delete(`/merchant-rules/${encodeURIComponent(key)}`);
    if (editingRuleKey === key) resetRuleForm();
    load();
  };

  return (
    <Shell>
      <div className="mb-6">
        <h1 className="font-display text-4xl font-bold text-slate-900 dark:text-slate-100">Settings</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Profile, budgets and recurring transactions</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Suit switcher spans full width */}
        <div className="md:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100">Pick your suit</h3>
            <span className="text-[11px] tracking-widest uppercase font-bold spidey-red">Theme</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Change the vibe of your dashboard, buttons and accents.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {SUITS.map(s => {
              const active = suit === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => { setSuit(s.id); toast.success(`${s.name} suit equipped`); }}
                  data-testid={`suit-${s.id}-btn`}
                  className={`text-left rounded-2xl p-4 border-2 transition-all duration-200 hover:-translate-y-0.5 relative overflow-hidden ${active ? "border-slate-900 dark:border-white shadow-lg" : "border-slate-200 dark:border-slate-800"}`}
                  style={{ background: `linear-gradient(135deg, ${s.navy} 0%, ${s.primary} 130%)` }}
                >
                  <div className="flex items-center gap-3 relative">
                    <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur grid place-items-center">
                      <SpiderLogo className="w-5 h-5" color="#ffffff" />
                    </div>
                    <div>
                      <div className="text-white font-display font-bold text-sm">{s.name}</div>
                      <div className="text-white/70 text-[11px] italic">{s.tag}</div>
                    </div>
                    {active && (
                      <div className="absolute right-0 top-0 w-6 h-6 rounded-full bg-white text-slate-900 grid place-items-center">
                        <Check className="w-3.5 h-3.5" />
                      </div>
                    )}
                  </div>
                  <div className="mt-4 flex items-center gap-1.5 relative">
                    <span className="w-4 h-4 rounded-full border border-white/30" style={{ background: s.primary }}></span>
                    <span className="w-4 h-4 rounded-full border border-white/30" style={{ background: s.navy }}></span>
                    <span className="text-[10px] text-white/70 ml-auto">{s.desc}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex items-center justify-between rounded-2xl border border-slate-200 dark:border-slate-800 px-4 py-3 bg-slate-50/50 dark:bg-slate-800/40">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-spidey-red text-white grid place-items-center">
                <Volume2 className="w-4 h-4" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Thwip sound effect</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">Play a soft web-shot when a transaction is saved</div>
              </div>
            </div>
            <Switch
              data-testid="sound-toggle"
              checked={soundOn}
              onCheckedChange={(v) => {
                setSoundEnabled(v);
                setSoundOn(v);
                if (v) { setTimeout(playThwip, 60); toast.success("Thwip on"); }
                else toast("Thwip off");
              }}
            />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6">
          <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">Profile</h3>
          <div className="space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Name</Label>
              <Input data-testid="profile-name-input" value={name} onChange={e => setName(e.target.value)} className="rounded-xl mt-1.5" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Monthly Budget</Label>
              <Input data-testid="profile-budget-input" type="number" step="0.01" value={budget} onChange={e => setBudget(e.target.value)} className="rounded-xl mt-1.5" />
            </div>
            <Button onClick={saveProfile} className="rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="save-profile-btn">Save changes</Button>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-xl bg-spidey-red text-white grid place-items-center">
              <KeyRound className="w-4 h-4" />
            </div>
            <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100">Change password</h3>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2 mb-4">
            Update the password on your email account. Signed in with Google only? Keep using the Google button — nothing to change here.
          </p>
          <div className="space-y-3">
            <div>
              <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Current password</Label>
              <Input data-testid="pw-current-input" type="password" autoComplete="current-password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} className="rounded-xl mt-1.5" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">New password</Label>
                <Input data-testid="pw-new-input" type="password" autoComplete="new-password" value={newPw} onChange={e => setNewPw(e.target.value)} className="rounded-xl mt-1.5" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase font-bold text-slate-500 dark:text-slate-400">Confirm new</Label>
                <Input data-testid="pw-confirm-input" type="password" autoComplete="new-password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="rounded-xl mt-1.5" />
              </div>
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">Minimum 8 characters. Choose something you don't use elsewhere.</div>
            <Button
              onClick={changePassword}
              disabled={pwSaving}
              className="rounded-full bg-spidey-red text-white hover:opacity-90 disabled:opacity-50"
              data-testid="pw-save-btn"
            >
              {pwSaving ? "Updating…" : "Update password"}
            </Button>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6">
          <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">Category Budgets & Alerts</h3>
          <div className="flex gap-2">
            <Select value={newCat} onValueChange={setNewCat}>
              <SelectTrigger data-testid="cat-budget-select" className="rounded-full flex-1"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>{cats.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="cat-budget-limit" type="number" step="0.01" placeholder="Limit" value={newLimit} onChange={e => setNewLimit(e.target.value)} className="rounded-full w-32" />
            <Button onClick={saveBudget} className="rounded-full bg-spidey-red text-white hover:opacity-90" data-testid="add-cat-budget-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="mt-4 space-y-2">
            {budgets.map(b => (
              <div key={b.id} data-testid={`budget-row-${b.id}`} className="flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800 last:border-none">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{b.category}</div>
                <div className="flex items-center gap-3">
                  <div className="tabular text-sm text-slate-600 dark:text-slate-400">{fmtMoney(b.monthly_limit, cur)}</div>
                  <button onClick={() => delBudget(b.id)} className="text-rose-500" data-testid={`del-budget-${b.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            {budgets.length === 0 && <div className="text-xs text-slate-500 dark:text-slate-400 py-2">No category budgets set.</div>}
          </div>
        </div>

        <div className="md:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6">
          <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
            <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100">Recurring Transactions</h3>
            {editingRecId && (
              <span
                data-testid="rec-editing-banner"
                className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
              >
                Editing
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            {editingRecId
              ? "Update the fields below and hit save to overwrite this recurring item."
              : "Items are auto-posted to your feed whenever their next date arrives. You can also post any due ones instantly."}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <Select value={rType} onValueChange={(v) => { setRType(v); if (!editingRecId) setRCat(""); }}>
              <SelectTrigger className="rounded-full" data-testid="rec-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">Expense</SelectItem>
                <SelectItem value="income">Income</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Amount" type="number" step="0.01" value={rAmt} onChange={e => setRAmt(e.target.value)} className="rounded-full" data-testid="rec-amt" />
            <Select value={rCat} onValueChange={setRCat}>
              <SelectTrigger className="rounded-full" data-testid="rec-cat"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                {(rType === "expense" ? allCats.expense : allCats.income).map(c => {
                  const meta = getCategoryMeta(c);
                  const Ico = meta.Icon;
                  return (
                    <SelectItem key={c} value={c}>
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-lg grid place-items-center ${meta.bg} ${meta.fg}`}>
                          <Ico className="w-3.5 h-3.5" />
                        </span>
                        {c}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <Input placeholder="Description" value={rDesc} onChange={e => setRDesc(e.target.value)} className="rounded-full" data-testid="rec-desc" />
            <Select value={rFreq} onValueChange={setRFreq}>
              <SelectTrigger className="rounded-full" data-testid="rec-freq"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="biweekly">Bi-weekly (every 2 weeks)</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly (every 3 months)</SelectItem>
                <SelectItem value="annually">Annually</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input type="date" value={rNext} onChange={e => setRNext(e.target.value)} className="rounded-full" data-testid="rec-next" />
              <Button
                onClick={saveRecurring}
                className="rounded-full bg-spidey-red text-white hover:opacity-90 shrink-0"
                data-testid={editingRecId ? "save-rec-edit-btn" : "add-rec-btn"}
                title={editingRecId ? "Save changes" : "Add recurring"}
              >
                {editingRecId ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </Button>
              {editingRecId && (
                <Button
                  onClick={resetRecForm}
                  variant="outline"
                  className="rounded-full shrink-0"
                  data-testid="cancel-rec-edit-btn"
                  title="Cancel edit"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {recurring.map(r => {
              const meta = getCategoryMeta(r.category);
              const Ico = meta.Icon;
              const isEditing = editingRecId === r.id;
              return (
              <div
                key={r.id}
                data-testid={`rec-row-${r.id}`}
                className={`flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800 last:border-none rounded-xl px-2 -mx-2 transition-colors ${isEditing ? "bg-amber-50/60 dark:bg-amber-500/10 border-amber-200/60" : ""}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${meta.bg} ${meta.fg}`}>
                    <Ico className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">{r.category} <span className="text-xs text-slate-500 dark:text-slate-400 font-normal">· {freqLabel(r.frequency)} · {r.type}</span></div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{r.description || "—"} · Next: {new Date(r.next_run).toLocaleDateString()}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className={`tabular text-sm font-semibold ${r.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>{fmtMoney(r.amount, cur)}</div>
                  <button
                    onClick={() => startEditRecurring(r)}
                    className="w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 grid place-items-center text-slate-500 dark:text-slate-400"
                    data-testid={`edit-rec-${r.id}`}
                    title="Edit recurring"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => delRecurring(r.id)}
                    className="w-8 h-8 rounded-full hover:bg-rose-50 dark:hover:bg-rose-500/10 grid place-items-center text-rose-500"
                    data-testid={`del-rec-${r.id}`}
                    title="Delete recurring"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              );
            })}
            {recurring.length === 0 && <div className="text-xs text-slate-500 dark:text-slate-400 py-2">No recurring items yet.</div>}
          </div>
          {recurring.length > 0 && (
            <div className="mt-4 flex justify-end">
              <Button onClick={runRecurring} variant="outline" className="rounded-full" data-testid="run-recurring-btn">Post due items now</Button>
            </div>
          )}
        </div>

        {/* Hidden auto-sync suggestions (per-debt, stored in localStorage) */}
        {hiddenDebts.length > 0 && (
          <div className="md:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6" data-testid="hidden-sync-card">
            <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
              <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100">
                Hidden auto-sync suggestions
              </h3>
              <Button
                variant="outline"
                onClick={restoreAllHidden}
                className="rounded-full text-xs"
                data-testid="restore-all-hidden-sync"
              >
                Restore all ({hiddenDebts.length})
              </Button>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              You've hidden the "Auto-sync payments" chip for {hiddenDebts.length} debt{hiddenDebts.length !== 1 ? "s" : ""}. This preference syncs across every device you sign into — restore any of them here to see the suggestions again.
            </p>
            <div className="space-y-2">
              {hiddenDebts.map(d => (
                <div key={d.id} className="flex items-center justify-between bg-slate-50 dark:bg-slate-800 rounded-2xl px-4 py-3" data-testid={`hidden-sync-row-${d.id}`}>
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{d.name}</div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => restoreHiddenDebt(d.id)}
                    className="rounded-full text-xs text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-500/20"
                    data-testid={`restore-hidden-sync-${d.id}`}
                  >
                    <Check className="w-3.5 h-3.5 mr-1" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="md:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6" data-testid="merchant-rules-card">
          <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
            <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100">Merchant Rules</h3>
            {editingRuleKey && (
              <span
                data-testid="rule-editing-banner"
                className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
              >
                Editing
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
            {editingRuleKey
              ? "Update the pattern or category below and hit save."
              : `Pin a merchant to a category — e.g. "Dutch Bros" → Food. Future imports and manual entries with a matching merchant will auto-categorize.`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
            <Input
              placeholder="Merchant (e.g. Dutch Bros)"
              value={rulePattern}
              onChange={(e) => setRulePattern(e.target.value)}
              className="rounded-full sm:col-span-3"
              data-testid="rule-pattern-input"
            />
            <Select value={ruleCategory} onValueChange={setRuleCategory}>
              <SelectTrigger className="rounded-full sm:col-span-2" data-testid="rule-category-select">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {allCats.expense?.map((c) => {
                  const meta = getCategoryMeta(c);
                  const Ico = meta.Icon;
                  return (
                    <SelectItem key={c} value={c}>
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-lg grid place-items-center ${meta.bg} ${meta.fg}`}>
                          <Ico className="w-3.5 h-3.5" />
                        </span>
                        {c}
                      </div>
                    </SelectItem>
                  );
                })}
                {allCats.income?.map((c) => (
                  <SelectItem key={`income-${c}`} value={c}>
                    <span className="text-emerald-600">↑</span> {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                onClick={saveRule}
                className="rounded-full bg-spidey-red text-white hover:opacity-90 flex-1"
                data-testid={editingRuleKey ? "save-rule-edit-btn" : "add-rule-btn"}
                title={editingRuleKey ? "Save changes" : "Add rule"}
              >
                {editingRuleKey ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </Button>
              {editingRuleKey && (
                <Button
                  onClick={resetRuleForm}
                  variant="outline"
                  className="rounded-full"
                  data-testid="cancel-rule-edit-btn"
                  title="Cancel edit"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {merchantRules.length === 0 && (
              <div className="text-xs text-slate-500 dark:text-slate-400 py-2">
                No rules yet. Rules are also auto-learned every time you edit a transaction's category.
              </div>
            )}
            {merchantRules.map((r) => {
              const meta = getCategoryMeta(r.category);
              const Ico = meta.Icon;
              const isEditing = editingRuleKey === r.merchant_key;
              return (
                <div
                  key={r.merchant_key}
                  data-testid={`rule-row-${r.merchant_key}`}
                  className={`flex justify-between items-center py-2 border-b border-slate-100 dark:border-slate-800 last:border-none rounded-xl px-2 -mx-2 transition-colors ${isEditing ? "bg-amber-50/60 dark:bg-amber-500/10 border-amber-200/60" : ""}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${meta.bg} ${meta.fg}`}>
                      <Ico className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                        {r.pattern || r.merchant_key}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        → <span className="font-medium text-slate-700 dark:text-slate-300">{r.category}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => startEditRule(r)}
                      className="w-8 h-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 grid place-items-center text-slate-500 dark:text-slate-400"
                      data-testid={`edit-rule-${r.merchant_key}`}
                      title="Edit rule"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteRule(r.merchant_key)}
                      className="w-8 h-8 rounded-full hover:bg-rose-50 dark:hover:bg-rose-500/10 grid place-items-center text-rose-500"
                      data-testid={`delete-rule-${r.merchant_key}`}
                      title="Delete rule"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {isAdmin && (
          <div data-testid="admin-system-status-card" className="md:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-spidey-red text-white grid place-items-center">
                  <Activity className="w-4 h-4" />
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-100">System status</h3>
                <span className="ml-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-spidey-red/10 spidey-red">Admin</span>
              </div>
              <button
                onClick={refreshHealth}
                data-testid="health-refresh-btn"
                className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400 hover:text-spidey-red"
              >
                Refresh
              </button>
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <div data-testid="health-status-tile" className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">Overall</div>
                <div className={`mt-2 text-lg font-display font-bold ${health?.status === "ok" ? "text-emerald-600" : "text-amber-600"}`}>
                  {health?.status || "…"}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">Database</div>
                <div className={`mt-2 text-lg font-display font-bold ${health?.db === "ok" ? "text-emerald-600" : "text-rose-600"}`}>
                  {health?.db || "…"}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{health?.db_latency_ms ?? "—"} ms</div>
              </div>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">Uptime</div>
                <div className="mt-2 text-lg font-display font-bold text-slate-900 dark:text-slate-100">
                  {health?.uptime_s != null ? `${Math.floor(health.uptime_s / 60)}m` : "—"}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">Version</div>
                <div className="mt-2 text-lg font-display font-bold text-slate-900 dark:text-slate-100">{health?.version || "—"}</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400 mb-2">Last 30 checks (newest → oldest)</div>
              <div data-testid="health-sparkline" className="flex items-end gap-0.5 h-8">
                {pings.length === 0 ? (
                  <div className="text-xs text-slate-400">Waiting for the first watchdog probe…</div>
                ) : (
                  pings.map((p, i) => (
                    <div
                      key={i}
                      title={`${p.time} — db=${p.db}${p.db_latency_ms != null ? ` (${p.db_latency_ms}ms)` : ""}`}
                      className={`w-1.5 rounded-sm ${p.db === "ok" ? "bg-emerald-500" : "bg-rose-500"}`}
                      style={{ height: `${Math.max(6, Math.min(32, (p.db_latency_ms ?? 8) + 6))}px` }}
                    />
                  ))
                )}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-3">
                Watchdog pings <code>/api/health</code> every 60 seconds and emails on <code>db != ok</code>.
                For external monitoring, point <a className="spidey-red hover:underline" href="https://uptimerobot.com/" target="_blank" rel="noreferrer">UptimeRobot</a> (or any HTTP monitor) at{" "}
                <code className="text-slate-700 dark:text-slate-200">/api/health</code> with keyword <code>"db":"ok"</code>.
              </div>
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}
